import { FC, useReducer, useEffect, ReactNode } from 'react';
import { StreamVideoClient } from '@stream-io/video-react-sdk';

import { api } from '@getstream/ui-library/build';

import { useGetStartedContext } from '../../Shared/GetStarted/GetStarted.context';

import Call from '../Call';
import Feedback from '../../Shared/Feedback';

import { videoReducer } from './VideoController.reducer';
import { initialState } from './VideoController.state';
import { VideoContextProvider } from './VideoController.context';

import { Actions } from '../../../types/video';

import clientCreated from '../../../images/video/client-created.svg';
import noCallId from '../../../images/video/no-call-id.svg';
import callCreated from '../../../images/video/call-id.svg';
import streamLoading from '../../../images/video/stream-loading.gif';

import styles from './VideoController.module.css';

const { apiGetPublicUserToken } = api;
const tokenProviderURL = import.meta.env.VITE_TOKEN_PROVIDER_URL;

export type Props = {
  children: ReactNode | undefined;
};
// eslint-disable-next-line sonarjs/cognitive-complexity
export const VideoController: FC<Props> = ({ children }) => {
  const [state, dispatch] = useReducer(videoReducer, initialState);
  const { state: getStartedState } = useGetStartedContext();

  useEffect(() => {
    if (typeof window === 'undefined') return; // Skip async for SSR

    (async () => {
      try {
        const data = await apiGetPublicUserToken(
          import.meta.env.VITE_API_BASEURL,
        );

        const url = new URL(tokenProviderURL);
        url.searchParams.set('api_key', import.meta.env.VITE_STREAM_KEY);
        url.searchParams.set('user_id', data.user_id);
        const res = await fetch(url.toString());
        const token = await res.json();

        dispatch({
          type: Actions.SetClientUser,
          ...data,
          api_key: import.meta.env.VITE_STREAM_KEY,
          user_name: data.user_id.split('-')[0],
          user_id: token.userId,
          user_token: token.token,
        });
      } catch (err) {
        window.Sentry?.captureException(err);
      }
    })();
  }, []);

  const setClient = async () => {
    try {
      if (state.client) return;
      // create video client
      const user = {
        id: state.userId,
        name: state.userName,
        image: `https://getstream.io/random_svg/?id=${state.userId}&name=${state.userName}`,
      };
      const token = state.userToken;

      const client = new StreamVideoClient({
        apiKey: state.apiKey,
        user,
        token,
        options: { logLevel: 'warn' },
      });

      dispatch({ type: Actions.SetClient, client });
    } catch (error) {
      window.Sentry?.captureException(error);
      dispatch({ type: Actions.SetError, error });
    }
  };

  const setLoading = (loading: boolean) =>
    dispatch({ type: Actions.SetLoading, loading });

  const setStep = (step: number) =>
    dispatch({ type: Actions.SetStep, step: step });

  const setCustomControls = (bool: boolean) =>
    dispatch({ type: Actions.SetCustomControls, customControls: bool });

  const setJoined = (bool: boolean) =>
    dispatch({ type: Actions.SetJoined, joined: bool });

  const setCall = () => {
    const { client, callId } = state;
    try {
      if (state.call) return;
      const call = client?.call('default', `${callId}`);
      dispatch({ type: Actions.SetCall, call });
    } catch (error) {
      dispatch({ type: Actions.SetError, error });
    }
  };

  const setCallState = (callState: object) =>
    dispatch({ type: Actions.SetCallState, callState });

  useEffect(() => {
    if (state.feedback) {
      const removeFeedbackTimer = setTimeout(
        () => dispatch({ type: Actions.ClearFeedback }),
        3000,
      );
      return () => {
        clearTimeout(removeFeedbackTimer);
      };
    }
  }, [state.feedback]);
  return (
    <>
      <div className={styles.context}>
        <VideoContextProvider
          state={state}
          value={{
            setClient,
            setLoading,
            setStep,
            setCall,
            setCustomControls,
            setJoined,
            setCallState,
          }}
        >
          <div className={styles.step}>{children}</div>
          <div className={styles.sidebar}>
            <div className={styles.container}>
              {state.feedback && (
                <Feedback type={state.feedback.type}>
                  {state.feedback.msg}
                </Feedback>
              )}
              <div className={styles.phone}>
                {state.client && state.call && getStartedState.current >= 3 && (
                  <Call
                    client={state.client}
                    call={state.call}
                    customControls={state.customControls}
                  />
                )}

                {state.client &&
                  state.call &&
                  getStartedState.current === 2 && (
                    <>
                      <img src={callCreated} className="w-[98px]" />
                      <p className="relative text-center mt-8 uppercase text-sm text-white opacity-50">
                        Call Created
                      </p>
                    </>
                  )}
                {state.client && getStartedState.current === 1 && (
                  <>
                    <img src={clientCreated} className="w-[98px]" />
                    <p className="relative text-center mt-8 uppercase text-sm text-white opacity-50">
                      Initialized
                    </p>
                  </>
                )}
                {state.client &&
                  getStartedState.current === 2 &&
                  !state.call && (
                    <>
                      <img src={noCallId} className="w-[98px]" />
                      <p className="relative text-center mt-8 uppercase text-sm text-white opacity-50">
                        No Call ID
                      </p>
                    </>
                  )}
                {!state.client && (
                  <>
                    <img src={streamLoading} className="w-[182px] h-auto" />
                    <p className="relative -top-8 text-center uppercase text-sm text-white opacity-30">
                      Waiting to initialize
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </VideoContextProvider>
      </div>
    </>
  );
};
